import _ from 'lodash';

import FormElement from './FormElement';
import StyleMixin from './mixins/styleMixins/StyleMixin';
import mixin from '../privates/utils/mixin';
import DisabledMixin from './mixins/DisabledMixin';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import FocusMixin from './mixins/FocusMixin';
import ClickableMixin from './mixins/ClickableMixin';
import * as Utils from './Utils';
import {nullIfNil} from '../privates/utils';
import {validate, validateRange} from '../privates/validations/validation';
import * as members from '../privates/privateMembers';
import valueSanitzationAlgorithm from '../privates/utils/valueSanitzationAlgorithm';
import {
    getOptionsFromFull,
    getOptionsFromFullWithChecked,
    optionsToDisplay,
    validateOption
} from '../privates/utils/optionsUtils';
import RequiredMixin from './mixins/RequiredMixin';
import {getCheckedValues, getCheckedIndices} from '../privates/utils/checkboxGroupUtils';
import {runValidationsAndSync, runValidationsAndSyncAfterChange} from '../privates/validationsService/validationsService';


/**
 * @class CheckboxGroup
 * @summary Checkbox groups are used for selecting any number of the given
 *  options. 
 * @description
 *  Checkbox groups consist of a list of [options](#Option). Each
 *  [option](#Option) contains a label, which is what the user sees, and a
 *  value, which is what is used in code and stored in your collections.
 * @mixes $w.FormElement
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.DisabledMixin
 * @mixes $w.FocusMixin
 * @mixes $w.ClickableMixin
 * @mixes $w.StyleMixin
 * @mixes $w.RequiredMixin
 * @memberof $w
 * @queryable
 * @viewername wysiwyg.viewer.components.inputs.CheckboxGroup
 * @definitionId wysiwyg.viewer.components.inputs.CheckboxGroup
 * @tagname CheckboxGroup
 */
class CheckboxGroup extends FormElement {

    /**
     * @typedef {external:Object} $w.CheckboxGroup~Option
     * @summary An object used by the `options` property that contains the attributes of a checkbox item.
     * @property {external:String} [value] The value of the checkbox option. This is what you use in code and is what is stored in your collections. Mandatory if `label` is not specified. 
     * @property {external:String} [label] The label of the checkbox option. This is what a user sees.  Mandatory if `value` is not specified.
     * @see [options](#options)
     * @see [value](#value)
     */

    /**
     * @member options
     * @label property
     * @syntax
     * get options(): Option[]
     * set options(value: options[]): void
     * @summary Sets or gets the options of a checkbox group.
     * @description
     *  Setting the `options` property sets all the [options](#Option)
     *  available to a user.
     *
     *  Getting the `options` property returns the current
     *  list of [options](#Option) available to a user.
     *
     *  You cannot modify the data array in-place. To add, change, or remove
     *  individual checkbox options:
     *
     *    1. Store the value of the `options` property in a variable.
     *    1. Make changes to the options array.
     *    1. Reset the `options` property with the modified array.
     * @snippet [CheckboxGroup-options_get.es6=Get the list of options and the first option's label and value from a checkbox group]
     * @snippet [CheckboxGroup-options_set.es6=Set the list of options for a checkbox group]
     * @snippet [CheckboxGroup-add.es6=Add an option to a checkbox group]
     *  This example retrieves the options of a checkbox group, adds a new
     *  option, and then overwrites the old options.
     * @type {$w.CheckboxGroup~Option[]}
     * @default An empty array
     * @memberof $w.CheckboxGroup
     * @instance
     */
    get options() {
        return getOptionsFromFull.call(this);
    }

  /**
   * @member options
   * @memberof $w.CheckboxGroup
   * @param {$w.CheckboxGroup~Option[]} newOptions
   */
    set options(newOptions) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;
        const isArrayValid = validate({
            compType,
            functionName: 'options',
            dataToValidate: {options: newOptions},
            compName: this.id
        });
        if (!isArrayValid) {
            return;
        }
        const arrItemsArrValid = _.every(newOptions, (v, i) => {
            return validate({
                compType,
                functionName: 'options',
                dataToValidate: {CheckboxInput: v},
                shouldCheckMandatoryFields: true,
                itemIndex: i,
                compName: this.id
            }) && validateOption(v, i, 'CheckboxInput');
        });
        if (!arrItemsArrValid) {
            return;
        }

        const oldCheckedByValue = _.reduce(getOptionsFromFullWithChecked.call(this), (checkedByValue, {value, checked}) => {
            checkedByValue[value] = checked;
            return checkedByValue;
        }, {});
        const options = nullIfNil(newOptions) ? optionsToDisplay(newOptions) : [];
        const value = _.get(RMI.getState(compId), 'value', []);
        const modifiedOptions = options.map((option) => {
            return {
                value: option.value,
                label: option.label,
                checked: _.get(oldCheckedByValue, option.value, _.includes(value, option.value)),
                type: 'CheckboxInput'
            };
        });

        runValidationsAndSync(RMI, compId, getCheckedValues(modifiedOptions));
        Utils.setData(this, {options: modifiedOptions});
    }

    /**
     * @member value
     * @label property
     * @syntax
     * get value(): string[]
     * set value(value: string[]): void
     * @summary Sets or gets the value of the selected options.
     * @description
     *  Setting the `value` property sets the [`options`](#Option)
     *  with those values to be the selected options. You must set the `value`
     *  property to values that exist in the [`options`](#options) list.
     *
     *  To reset the checkbox group to have no options
     *  selected, set the `value` property to an empty array, `null` or `undefined`.
     *
     *  Getting the `value` property returns the values of
     *  the currently selected options. If no value is selected, the
     *  `value` property returns an empty array.
     * @note
     *  Changing a checkbox group's `value` in code does not trigger an `onChange` event.
     * @snippet [CheckboxGroup-value_get.es6=Get the values of the selected options]
     * @snippet [CheckboxGroup-value_set.es6=Set the selected options by values]
     * @type {external:String[]}
     * @memberof $w.CheckboxGroup
     * @override
     */
    get value() {
        return getCheckedValues(getOptionsFromFullWithChecked.call(this));
    }

  /**
   * @member value
   * @memberof $w.CheckboxGroup
   * @param {external:String[]} val
   */
    set value(val) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compData = RMI.getFullDataWithOverrides(compId);
        const compType = members._type.get(this).compType;
        const isValid = validate({
            compType,
            functionName: 'value',
            dataToValidate: {value: val},
            compName: this.id
        });

        if (!isValid) {
            return;
        }
        const value = valueSanitzationAlgorithm(compData, val);
        const newState = _.defaults({value}, RMI.getState(compId));

        RMI.updateModel({[compId]: {state: newState}});

        const modifiedOptions = this.options.map((option) => {
            return {
                value: option.value,
                label: option.label,
                checked: _.includes(value, option.value),
                type: 'CheckboxInput'
            };
        });

        runValidationsAndSyncAfterChange(RMI, compId, value);
        RMI.setData(compId, {options: modifiedOptions});
    }

    /**
     * @member selectedIndices
     * @label property
     * @syntax
     * get selectedIndices(): Number[]
     * set selectedIndices(value: Number[]): void
     * @summary Sets or gets the indices of the selected options.
     * @description
     *  Setting the `selectedIndices` property sets the options at
     *  each specified index to be a selected option.
     *
     *  To reset the checkbox group to have no option
     *  selected, set the `selectedIndices` property to an empty array, null, or `undefined`.
     *
     *  Getting the `selectedIndices` property returns an array of the indices of
     *  all selected options. If no value is selected, the `selectedIndices`
     *  property returns an empty array.
     * @type {external:Number[]}
     * @snippet [CheckboxGroup-selectedIndices_get.es6=Get the indices of the selected options]
     * @snippet [CheckboxGroup-selectedIndices_set.es6=Select the selected options by indices]
     * @memberof $w.CheckboxGroup
     * @instance
     * @attr {external:String}
     */
    get selectedIndices() {
        return getCheckedIndices(getOptionsFromFullWithChecked.call(this));
    }

  /**
   * @member selectedIndices
   * @memberof $w.CheckboxGroup
   * @param {external:Number[]} indices
   */
    set selectedIndices(indices) {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const compType = members._type.get(this).compType;

        const options = getOptionsFromFull.call(this);
        const acceptNil = true;
        if (!validate({
                compType,
                functionName: 'selectedIndices',
                dataToValidate: {selectedIndices: indices},
                compName: this.id
            }) || _.some(indices, (index) => !validateRange({
                functionName: 'selectedIndices',
                propertyName: 'selectedIndices',
                value: index,
                minValue: 0,
                maxValue: options.length - 1,
                acceptNil
            }))) {
            return;
        }

        const value = [];

        const modifiedOptions = _.map(options, (option, index) => {
            const checked = _.includes(indices, index);
            if (checked) {
                value.push(option.value);
            }
            return _.assign(option, {checked, type: 'CheckboxInput'});
        });
        const newState = _.defaults({value}, RMI.getState(compId));
        RMI.updateModel({[compId]: {state: newState}});

        runValidationsAndSyncAfterChange(RMI, compId, getCheckedValues(modifiedOptions));
        Utils.setData(this, {options: modifiedOptions});
    }

    toJSON() {
        const {id, type, global, rendered, required, value, options, selectedIndices} = this;
        return {id, type, global, rendered, required, value, options, selectedIndices};
    }

    /**
     * @member style
     * @label property
     * @syntax
     * get style(): Style
     * @summary Gets an object containing information about the checkbox group's styles.
     * @description
     *  The following styles can be used with a checkbox group:
     *
     *  + [`backgroundColor`]($w.Style.html#backgroundColor)
     *  + [`borderColor`]($w.Style.html#borderColor)
     *  + [`color`]($w.Style.html#color)
     *
     * 
     * 
     * 
     *  Getting or setting a checkbox group's styles, gets or sets the styles of the
     *  checkbox group's regular state. It does not set the styles of the checkbox group's hover,
     *  focus, error, or disabled states.
     * 
     * 
     * 
     * @snippet [BackgroundColorMixin-backgroundColor_set.es6=Set the background color]
     * @snippet [BackgroundColorMixin-backgroundColor_get.es6=Get the background color]
     * @type {$w.Style}
     * @memberof $w.CheckboxGroup
     * @readonly
     * @override
     */
}

mixin(CheckboxGroup.prototype, HiddenCollapsedMixin.prototype);
mixin(CheckboxGroup.prototype, DisabledMixin.prototype);
mixin(CheckboxGroup.prototype, FocusMixin.prototype);
mixin(CheckboxGroup.prototype, ClickableMixin.prototype);
mixin(CheckboxGroup.prototype, StyleMixin.prototype);
mixin(CheckboxGroup.prototype, RequiredMixin.prototype);

export default CheckboxGroup;
